import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ServerCog, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Stat } from "@/pages/admin/Stat";

interface HealthData {
  git_sha: string | null;
  build_time: string | null;
  server_time: string;
  workspace_id: string;
  api_errors_last_24h: number;
  api_errors_last_7d: number;
  retention_days: number;
  connected_accounts: Array<{
    account_id: string;
    platform: string;
    account_type?: string;
    display_name?: string;
    handle?: string;
    connected?: boolean;
    is_oauth?: boolean;
    last_synced_at?: string | null;
    last_error?: string | null;
  }>;
  last_account_error: Record<string, {
    endpoint: string;
    http_status?: number;
    reason?: string;
    detail?: string;
    created_at: string;
  }>;
}

/**
 * Deployment health card — live build info + per-account health.
 * Refreshes every 30s. Surfaces the most recent API error per account so
 * admins can spot broken OAuth tokens or rate-limit failures at a glance.
 */
export const HealthCard: React.FC = () => {
  const q = useQuery<HealthData>({
    queryKey: ["admin-health"],
    queryFn: async () => (await api.get("/admin/health")).data,
    refetchInterval: 30_000,
  });
  const d = q.data;
  return (
    <div
      data-testid="admin-health-card"
      className="bg-white rounded-xl p-6 border border-[color:var(--sg-border)] mt-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <ServerCog className="h-4 w-4 text-[color:var(--sg-violet)]" />
        <h2 className="text-lg font-semibold text-[color:var(--sg-fg)]">Deployment Health</h2>
      </div>
      <p className="text-sm text-[color:var(--sg-muted)] mb-4">
        Live build info + per-account health. Refreshes every 30 seconds.
      </p>
      {q.isLoading || !d ? (
        <div className="sg-skeleton h-32" />
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-3 text-xs">
            <Stat label="Git SHA" value={d.git_sha || "—"} testId="health-git-sha" mono />
            <Stat
              label="Server time"
              value={new Date(d.server_time).toLocaleString()}
              testId="health-server-time"
            />
            <Stat
              label="API errors · 24 h"
              value={String(d.api_errors_last_24h)}
              testId="health-errors-24h"
              danger={d.api_errors_last_24h > 0}
            />
            <Stat
              label="API errors · 7 d"
              value={String(d.api_errors_last_7d)}
              testId="health-errors-7d"
              danger={d.api_errors_last_7d > 0}
            />
          </div>

          <div className="mt-5">
            <h3 className="text-xs font-semibold text-[color:var(--sg-fg)] mb-2 uppercase tracking-wide">
              Connected accounts ({d.connected_accounts.length})
            </h3>
            <div className="border border-[color:var(--sg-border)] rounded-md divide-y divide-[color:var(--sg-border)] overflow-hidden">
              {d.connected_accounts.length === 0 && (
                <div className="text-xs text-[color:var(--sg-muted)] px-3 py-4 text-center">
                  No accounts connected yet.
                </div>
              )}
              {d.connected_accounts.map((a) => {
                const err = d.last_account_error[a.account_id];
                return (
                  <div
                    key={a.account_id}
                    data-testid={`health-account-${a.account_id}`}
                    className="px-3 py-2 flex flex-wrap items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-[color:var(--sg-fg)] truncate">
                        {a.display_name || a.handle || a.account_id}
                        <span className="ml-2 text-[10px] uppercase text-[color:var(--sg-muted)]">
                          {a.platform} · {a.account_type} {a.is_oauth ? "· oauth" : "· mock"}
                        </span>
                      </div>
                      <div className="text-[11px] text-[color:var(--sg-muted)]">
                        Last synced:{" "}
                        {a.last_synced_at
                          ? new Date(a.last_synced_at).toLocaleString()
                          : "never"}
                      </div>
                    </div>
                    {err ? (
                      <div
                        className="text-[10px] font-mono bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1 max-w-md truncate"
                        title={err.detail || err.reason}
                        data-testid={`health-account-error-${a.account_id}`}
                      >
                        {err.http_status ? `${err.http_status} ` : ""}
                        {err.reason || err.endpoint}
                      </div>
                    ) : (
                      <div className="text-[10px] text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        No recent errors
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
